import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowUpRight, FiMenu, FiX } from "react-icons/fi";

const navLinks = [
  { name: "About", href: "#about" },
  { name: "Work", href: "#projects" },
  { name: "Skills", href: "#skills" },
  { name: "Journey", href: "#journey" },
  { name: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Highlight the section currently filling the viewport. */
  useEffect(() => {
    const sections = navLinks
      .map((link) => document.querySelector(link.href))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  /* Lock the page while the mobile sheet is open. */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition duration-500 ${
          scrolled
            ? "border-b border-cream/10 bg-ink/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="shell flex h-[76px] items-center justify-between px-5 sm:px-8">
          <a
            href="#home"
            className="display text-xl tracking-tight text-cream"
            aria-label="Back to top"
          >
            Albina<span className="text-sage">.</span>
          </a>

          <div className="hidden items-center gap-9 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`link-underline text-[13px] font-medium tracking-wide transition-colors ${
                  active === link.href
                    ? "text-mint"
                    : "text-cream/55 hover:text-cream"
                }`}
              >
                {link.name}
              </a>
            ))}
          </div>

          <a
            href="#contact"
            className="group hidden items-center gap-2 rounded-full border border-mint/25 bg-mint/10 px-5 py-2.5 text-[13px] font-semibold text-mint transition duration-300 hover:border-mint/60 hover:bg-mint hover:text-ink md:inline-flex"
          >
            Let’s talk
            <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/15 text-cream md:hidden"
          >
            <FiMenu className="text-lg" />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md md:hidden"
          >
            <div className="flex h-[76px] items-center justify-between px-5">
              <span className="display text-xl text-cream">
                Albina<span className="text-sage">.</span>
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/15 text-cream"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="flex flex-col gap-2 px-6 pt-10">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * index, duration: 0.4 }}
                  className="flex items-baseline gap-4 border-b border-cream/10 py-5"
                >
                  <span className="font-mono text-[11px] text-sage/60">
                    0{index + 1}
                  </span>
                  <span className="display text-4xl text-cream">
                    {link.name}
                  </span>
                </motion.a>
              ))}

              <motion.a
                href="#contact"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.34, duration: 0.4 }}
                className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-mint px-6 py-4 text-sm font-semibold text-ink"
              >
                Start a conversation
                <FiArrowUpRight />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
